import { color } from '@central/ui';
import { muted } from '../lib/labels';

const W = 600;
const H = 120;
const DAY_MS = 86_400_000;

export interface Point {
  /** RFC 3339 start of the hour. */
  at: string;
  value: number;
}

/**
 * One series over the 24 hours before `now`, from 0 (bottom) to `max`
 * (top), in the AdminEnvironment chart style. Hours with no point are a gap
 * in time, not a drop to zero: the line joins the points it has.
 */
export function LineChart24h({
  points,
  max,
  label,
  now = new Date(),
}: {
  points: Point[];
  max: number;
  label: string;
  now?: Date;
}) {
  const start = now.getTime() - DAY_MS;
  const d = points
    .map((p, i) => {
      const x = ((new Date(p.at).getTime() - start) / DAY_MS) * W;
      const y = H - (Math.min(Math.max(p.value, 0), max) / max) * H;
      return `${i ? 'L' : 'M'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: H, display: 'block' }}
        role="img"
        aria-label={label}
      >
        {[30, 60, 90].map((y) => (
          <line key={y} x1={0} y1={y} x2={W} y2={y} stroke={color.rowRule} />
        ))}
        <line x1={0} y1={H - 1} x2={W} y2={H - 1} stroke={color.borderStrong} />
        {points.length > 0 && (
          <path
            d={d}
            fill="none"
            stroke={color.link}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, ...muted }}>
        <span>24 h ago</span>
        <span>now</span>
      </div>
    </div>
  );
}
